import { useEffect, useMemo, useRef } from "react";
import { DocumentType, gql } from "__generated__/gql";
import { useBrandQuery } from "utils/apollo/hooks/brand";
import { ReceivedEmailMessageFilter } from "__generated__/gql/graphql";
import {
  BASE_COLUMNS,
  EmailTable,
  EmailTableRow,
  useEmailTableState,
} from "components/Shared/EmailTable";
import { useCurrentBrandId } from "utils/currentUserHooks";
import useCombineFilters from "utils/useCombineFilters";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { formatBooleanCell } from "components/Shared/ChakraReactTable/formatters";
import {
  RECEIVED_EMAIL_CATEGORY_COLUMN,
  RECEIVED_EMAIL_CATEGORY_ORDER_BY,
} from "components/Shared/EmailTable/receivedEmailCategoryColumn";

export const EMAIL_POLL_INTERVAL = 60000; // 1 minute

const query = gql(/* GraphQL */ `
  query BrandEmailInbox(
    $limit: Int!
    $offset: Int!
    $filter: ReceivedEmailMessageFilter
    $orderBy: [ReceivedEmailMessagesOrderBy!] = [EMAIL_MESSAGE__SENT_AT_DESC]
  ) {
    receivedEmailMessages(
      first: $limit
      offset: $offset
      orderBy: $orderBy
      filter: $filter
    ) {
      totalCount
      nodes {
        ...EmailTableColumns
        ...EmailTableCategoryColumn
        receivedEmailMessageCompletion {
          receivedEmailMessageId
        }
      }
    }
  }
`);

type TableRow = NonNullable<
  DocumentType<typeof query>["receivedEmailMessages"]
>["nodes"][number] &
  EmailTableRow;

const col = createColumnHelper<TableRow>();

const COLUMNS = [
  ...BASE_COLUMNS,
  RECEIVED_EMAIL_CATEGORY_COLUMN,
  col.accessor(
    (r) => !!r.receivedEmailMessageCompletion?.receivedEmailMessageId,
    {
      id: "isComplete",
      header: "Complete?",
      cell: formatBooleanCell,
      size: 95,
      enableSorting: false,
      meta: { props: { textAlign: "center" } },
    },
  ),
] as ColumnDef<TableRow, unknown>[];

export type TableProps = {
  filter?: ReceivedEmailMessageFilter;
  isReady?: boolean;
  selected?: UUID | null;
  onInitialIsEmpty?: (isEmpty: boolean) => void;
  onSelectedChange: (emailId: UUID) => void;
};

export default function Table({
  filter: filterProp,
  isReady = true,
  onInitialIsEmpty,
  selected,
  onSelectedChange,
}: TableProps) {
  const brandId = useCurrentBrandId();
  const didReportInitialIsEmpty = useRef(false);

  const brandFilter = useMemo<ReceivedEmailMessageFilter | undefined>(() => {
    if (!brandId) return;
    return { receivedEmailMessageBrand: { brandId: { equalTo: brandId } } };
  }, [brandId]);

  const filter = useCombineFilters<ReceivedEmailMessageFilter>(
    brandFilter,
    filterProp,
  );

  const { tableProps, limitOffset, orderBy, isPendingReset } =
    useEmailTableState({
      pageSize: 10,
      orderByEnumsById: RECEIVED_EMAIL_CATEGORY_ORDER_BY,
      defaultSorting: [{ id: "receivedAt", desc: true }],
      filter,
    });

  const {
    data: rawData,
    loading,
    refetch,
  } = useBrandQuery(query, {
    skip: !isReady || isPendingReset || !brandId,
    variables: { filter, orderBy, ...limitOffset },
    pollInterval: EMAIL_POLL_INTERVAL,
  });

  const isLoading =
    typeof window === "undefined" || loading || !isReady || isPendingReset;
  const totalCount = rawData?.receivedEmailMessages?.totalCount;

  useEffect(() => {
    if (didReportInitialIsEmpty.current) return;
    if (isLoading || typeof totalCount !== "number") return;
    didReportInitialIsEmpty.current = true;
    onInitialIsEmpty?.(totalCount === 0);
  }, [isLoading, onInitialIsEmpty, totalCount]);

  return (
    <EmailTable<TableRow>
      columns={COLUMNS}
      data={rawData?.receivedEmailMessages?.nodes}
      totalCount={totalCount}
      isLoading={isLoading}
      selected={selected}
      onSelectedChange={onSelectedChange}
      onRefetch={refetch}
      {...tableProps}
    />
  );
}
